import React, { useState, useEffect, useCallback } from 'react';
import { useGoals } from '../../contexts/GoalContext';
import { useLast10 } from '../../contexts/Last10Context';
import { getTasksByGoal } from '../../services/firebaseStorage';
import Last10Badge from './Last10Badge';
import FinishModePanel from './FinishModePanel';
import { logFinishModeActivated } from '../../services/analytics';

const GoalCard = ({ goal, onEdit, onDelete, onViewTasks }) => {
    const { getProgress, tasksVersion } = useGoals();
    const { isInLast10, enterLast10, activateFinishMode, deactivateFinishMode, finishModeGoalId } = useLast10();

    const [progress, setProgress] = useState({
        totalTasks: 0,
        completedTasks: 0,
        completionPercentage: 0
    });
    const [loadingProgress, setLoadingProgress] = useState(true);
    const [goalTasks, setGoalTasks] = useState([]);

    // Whether Finish Mode panel is open for THIS specific goal
    const finishModeOpen = finishModeGoalId === goal.id;

    useEffect(() => {
        const loadProgress = async () => {
            try {
                setLoadingProgress(true);
                const progressData = await getProgress(goal.id);
                setProgress(progressData);

                // Detect first-time Last 10% entry (goal doc doesn't have last10EnteredAt yet)
                if (
                    goal.status !== 'completed' &&
                    !goal.last10EnteredAt &&
                    progressData.completionPercentage < 100
                ) {
                    // Trigger if >= 80% OR if only 1 task remains
                    const percentageThresholdMet = progressData.completionPercentage >= 80;
                    const onlyOneTaskRemaining = progressData.totalTasks > 0 &&
                        (progressData.totalTasks - progressData.completedTasks) === 1;

                    if (percentageThresholdMet || onlyOneTaskRemaining) {
                        await enterLast10(goal.id, progressData.completionPercentage, goal.calendarId);
                    }
                }
            } catch (error) {
                console.error('Error loading progress:', error);
            } finally {
                setLoadingProgress(false);
            }
        };

        loadProgress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goal.id, goal.status, goal.last10EnteredAt, getProgress, tasksVersion]);

    const handleOpenFinishMode = useCallback(async () => {
        try {
            const tasks = await getTasksByGoal(goal.id, goal.calendarId);
            setGoalTasks(tasks);
            await activateFinishMode(goal.id);
            logFinishModeActivated({ goal_period: goal.period });
        } catch (err) {
            console.error('Error opening finish mode:', err);
        }
    }, [goal.id, goal.calendarId, goal.period, activateFinishMode]);

    const handleCloseFinishMode = useCallback(() => {
        deactivateFinishMode(goal.id);
    }, [goal.id, deactivateFinishMode]);

    const getPeriodBadge = (period) => {
        const badges = {
            yearly: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-100 border border-blue-300 dark:border-blue-700',
            quarterly: 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-100 border border-purple-300 dark:border-purple-700',
            monthly: 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-100 border border-green-300 dark:border-green-700'
        };
        return badges[period] || badges.yearly;
    };

    const getPeriodLabel = (period) => {
        const labels = {
            yearly: 'Yearly',
            quarterly: 'Quarterly',
            monthly: 'Monthly'
        };
        return labels[period] || period;
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
            completed: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
            archived: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200'
        };
        return badges[status] || badges.active;
    };

    const inLast10 = isInLast10(progress, goal);

    const getProgressColor = (percentage) => {
        if (inLast10) return 'bg-amber-500';
        if (percentage >= 75) return 'bg-green-500';
        if (percentage >= 50) return 'bg-blue-500';
        if (percentage >= 25) return 'bg-yellow-500';
        return 'bg-gray-400';
    };

    const formatPeriodDetails = () => {
        if (goal.period === 'yearly') {
            return `Year ${goal.year}`;
        } else if (goal.period === 'quarterly') {
            return `Q${goal.quarter} ${goal.year}`;
        } else if (goal.period === 'monthly') {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${monthNames[goal.month - 1]} ${goal.year}`;
        }
        return '';
    };

    const getBorderColor = (color) => {
        // Last 10% state overrides the goal's own colour with amber
        if (inLast10) return 'border-amber-400';
        const colors = {
            blue: 'border-blue-500',
            green: 'border-green-500',
            purple: 'border-purple-500',
            red: 'border-red-500',
            orange: 'border-orange-500',
            pink: 'border-pink-500',
            teal: 'border-teal-500',
            indigo: 'border-indigo-500'
        };
        return colors[color] || colors.blue;
    };

    // Subtle amber background tint for last10 cards
    const cardBg = inLast10
        ? 'bg-amber-50/60 dark:bg-amber-900/10'
        : 'bg-white dark:bg-gray-800';

    return (
        <>
            <div className={`${cardBg} rounded-lg shadow-md border-l-4 ${getBorderColor(goal.color)} p-4 hover:shadow-lg transition-shadow`}>
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getPeriodBadge(goal.period)}`}>
                                {getPeriodLabel(goal.period)}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(goal.status)}`}>
                                {goal.status}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatPeriodDetails()}
                            </span>
                            {/* Last 10% badge — only shown when in final phase */}
                            {!loadingProgress && inLast10 && (
                                <Last10Badge
                                    completionPercentage={progress.completionPercentage}
                                    goal={goal}
                                />
                            )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            {goal.title}
                        </h3>
                        {goal.description && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                {goal.description}
                            </p>
                        )}
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                        <span>Progress</span>
                        <span className="font-medium">
                            {loadingProgress ? '...' : `${progress.completionPercentage}%`}
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div
                            className={`h-2.5 rounded-full transition-all ${getProgressColor(progress.completionPercentage)}`}
                            style={{ width: `${progress.completionPercentage}%` }}
                        ></div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {loadingProgress ? (
                            'Loading...'
                        ) : (
                            `${progress.completedTasks} of ${progress.totalTasks} tasks completed`
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                    {/* Finish Mode CTA — only rendered in Last 10% state */}
                    {inLast10 && (
                        <button
                            onClick={handleOpenFinishMode}
                            className="w-full px-3 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded transition-colors flex items-center justify-center gap-2"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />
                            Enter Finish Mode
                        </button>
                    )}

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onViewTasks(goal)}
                            className="flex-1 px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                        >
                            View Tasks ({progress.totalTasks})
                        </button>
                        <button
                            onClick={() => onEdit(goal)}
                            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            aria-label="Edit goal"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </button>
                        <button
                            onClick={() => onDelete(goal)}
                            className="px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            aria-label="Delete goal"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Finish Mode overlay — rendered outside card flow so it covers full viewport */}
            {finishModeOpen && (
                <FinishModePanel
                    goal={goal}
                    progress={progress}
                    tasks={goalTasks}
                    onClose={handleCloseFinishMode}
                />
            )}
        </>
    );
};

export default GoalCard;
